import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { BANKBOX_SEARCH_TYPE_ARR } from "../../../utils/constant.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Header from "../../../layouts/Header.jsx";
import SingleSelectDropDown from "../../../layouts/SingleSelectDropDown.jsx";
import { axios_instance } from "../../../api/axios_instance.js";
import RefreshSpin from "../../../layouts/RefreshSpin.jsx";
import BankboxTable from "../../../layouts/BankboxTable.jsx";
import { resetBankBoxSortingColumns } from "../../../utils/constant.js";
import ManualActionModal from "./ManualActionModal.jsx";
import { ToastContainer, toast } from "react-toastify";
import ReasonsModal from "./ReasonsModal.jsx";
import {
  decryptRequest,
  encryptRequest,
} from "../../../utils/encryptdecryptrequest.js";
import MultiSelectDropDownReset from "../../../layouts/MultiSelectDropdownReset.jsx";

const BankBoxPanel = () => {
  const initialFilterValues = useMemo(
    () => ({
      searchType: "Loan ID",
      searchTypeInput: "",
      date_from: "",
      date_to: "",
      statuses: [],
      banks: "",
    }),
    [],
  );

  const initialTabData = {
    caseData: [],
    tabCounts: [0, 0, 0, 0, 0, 0, 0],
    total: 0,
  };

  const initialDropDownData = {
    statuses: [],
    banks: [],
  };

  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [dropdownData, setDropdownData] = useState(initialDropDownData);
  const [resetClicked, setResetClicked] = useState(false);

  const [tabData, setTabData] = useState(initialTabData);

  const [sortingColumns, setSortingColumns] = useState({
    ...resetBankBoxSortingColumns,
    lead_id: -1,
  });

  const [selectedSortingCol, setSelectedSortingCol] = useState({
    lead_id: -1,
  });
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);

  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [showArrowTopButton, setShowArrowTopButton] = useState(false);

  const [propsState, setPropsState] = useState({
    showReasonModal: false,
    showManualActionModal: false,
    manualActionProps: {},
    reasonModalProps: {},
    manualActionBtnDisabled: {},
  });

  const filterValuesRef = useRef(filterValues);

  const dropdownContainer = useRef(null);
  const dropdownContainers = useMemo(
    () => [dropdownContainer],
    [dropdownContainer],
  );

  const getAllTransactionsCount = async (
    filterValues = filterValuesRef.current,
  ) => {
    try {
      setTabLoading(true);
      const encryptedData = encryptRequest({
        filterValues,
        activeTab,
      });

      const res = await axios_instance({
        url: "/bankbox/getAllTransactionsCount",
        method: "POST",
        data: { reqData: encryptedData },
      });

      const { ...tabs } = decryptRequest(res.data.data);

      setTabData((prevState) => {
        const tabCounts = [...prevState.tabCounts];

        for (const key in tabs) {
          tabCounts[parseInt(key)] = tabs[key];
        }

        const newState = { ...prevState, tabCounts };
        return newState;
      });
    } catch (error) {
      console.log(error);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchListData = useCallback(
    async (toastObj) => {
      if (toastObj && Object.keys(toastObj).length && toastObj.error) {
        return toast.error(toastObj.msg, { theme: "colored" });
      }

      setIsLoading(true);

      try {
        const reqData = encryptRequest({
          filterValues: filterValuesRef.current,
          sortingColumns: selectedSortingCol,
          page,
          activeTab,
        });

        const response = await axios_instance({
          url: "/bankbox/getAllTransactions",
          method: "POST",
          data: { reqData },
        });

        const { pageOfResults: caseData, total } = decryptRequest(
          response.data.data,
        );

        setTabData((prevState) => {
          const tabCounts = [...prevState.tabCounts];
          tabCounts[activeTab] = total;

          const newState = {
            ...prevState,
            caseData,
            total,
            tabCounts,
          };

          return newState;
        });

        setPropsState((prevState) => ({
          ...prevState,
          showManualActionModal: false,
          showReasonModal: false,
        }));

        if (toastObj && Object.keys(toastObj).length) {
          toast.success(toastObj.msg, { theme: "colored" });
        }
      } catch (error) {
        if (error.response && error.response.status !== 500) {
        }
      } finally {
        setIsLoading(false);
      }
    },
    [selectedSortingCol, page, activeTab],
  );

  const fetchDropDownData = useCallback(async () => {
    try {
      const res = await axios_instance({
        url: "/bankbox/fetchBankboxPanelDropdown",
        method: "GET",
      });

      if (res?.status === 200) {
        const { statuses, banks } = decryptRequest(res?.data?.data);
        setDropdownData((prevState) => ({ statuses, banks }));
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  const closeAllDropdowns = useCallback(
    (target) => {
      dropdownContainers
        .filter((container) => container !== target)
        .forEach((container) => {
          const ul = container.current;
          if (ul && ul.classList.contains("visible")) {
            ul.classList.remove("visible");
            ul.style.display = "none";
          }
        });
    },
    [dropdownContainers],
  );

  const handleDocumentClick = useCallback(
    (e) => {
      // Check if the click was outside the dropdown container
      if (
        !dropdownContainers.some(
          (container) =>
            container.current && container.current.contains(e.target),
        )
      ) {
        closeAllDropdowns();
      }
    },
    [closeAllDropdowns, dropdownContainers],
  );

  useEffect(() => {
    fetchListData();

    document.addEventListener("click", handleDocumentClick);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [fetchListData, handleDocumentClick]);

  useEffect(() => {
    fetchDropDownData();
    getAllTransactionsCount();
  }, [fetchDropDownData]);

  const handleScroll = () => {
    if (window.scrollY > 100) setShowArrowTopButton(true);
    else setShowArrowTopButton(false);
  };

  const handleDateChange = (value, filterName) => {
    setFilterValues((prevState) => {
      const newFilterValues = {
        ...prevState,
        [filterName]: value,
      };

      filterValuesRef.current = newFilterValues;
      return newFilterValues;
    });
  };

  const handleCheckboxChange = (e, filterName) => {
    const { name, checked, value } = e.target;
    let temp = null;

    if (name === "selectall") {
      if (checked)
        temp = dropdownData[filterName].map(({ value }) => parseInt(value));
      else temp = [];
    } else {
      temp = [...filterValues[filterName]];

      if (checked) temp.push(parseInt(value));
      else temp = temp.filter((elem) => elem !== parseInt(value));
    }

    setFilterValues((prevState) => {
      const newFilterValues = {
        ...prevState,
        [filterName]: temp,
      };

      filterValuesRef.current = newFilterValues;
      return newFilterValues;
    });
  };

  const handleSorting = async (filterName) => {
    let col = sortingColumns;
    const order = [0, -1].includes(col[filterName]) ? 1 : -1;

    col = { ...resetBankBoxSortingColumns };
    col[filterName] = order;
    setSortingColumns(col);

    setSelectedSortingCol({ [filterName]: order });
  };

  const toggleDropDown = (container, e) => {
    closeAllDropdowns(container);
    const ul = container.current;
    if (ul.classList.contains("visible")) {
      ul.classList.remove("visible");
      ul.style.display = "none";
    } else {
      ul.classList.add("visible");
      ul.style.display = "block";
    }
    e.stopPropagation();
  };

  useEffect(() => {
    if (resetClicked) {
      setFilterValues(() => {
        const newFilterValues = initialFilterValues;
        filterValuesRef.current = newFilterValues;

        return newFilterValues;
      });

      setSortingColumns({ ...resetBankBoxSortingColumns, lead_id: -1 });
      setActiveTab(0);

      setPage(1);
      setSelectedSortingCol({ lead_id: -1 });

      setResetClicked(false);
    }
  }, [resetClicked, initialFilterValues]);

  const resetFunc = async (e) => {
    e.preventDefault();
    setResetClicked(true);

    getAllTransactionsCount(initialFilterValues);
  };

  const navSelected = (e, id) => {
    e.preventDefault();

    setActiveTab(id);
    setPage(1);

    setSortingColumns({ ...resetBankBoxSortingColumns, lead_id: -1 });
  };

  const pageSelected = (e, pageNo) => {
    e.preventDefault();
    setPage(pageNo);
  };

  const prevPage = (e) => {
    e.preventDefault();
    setPage((prevPage) => prevPage - 1);
  };

  const nextPage = (e) => {
    e.preventDefault();
    setPage((prevPage) => prevPage + 1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilterValues((prevFilterValues) => {
      const newFilterValues = {
        ...prevFilterValues,
        [name]: value,
      };

      filterValuesRef.current = newFilterValues;
      return newFilterValues;
    });
  };

  return (
    <span>
      <ToastContainer
        closeOnClick={false}
        draggable={false}
        closeButton={false}
      />
      <Header
        isNavigationModal={showNavigationModal}
        closeNavigationModal={() => setShowNavigationModal(false)}
        openNavigationModal={() => setShowNavigationModal(true)}
      ></Header>

      <ManualActionModal
        manualActionProps={propsState.manualActionProps}
        bankBoxPanelState={propsState}
        setBankBoxPanelState={setPropsState}
        fetchListData={fetchListData}
        closeManualActionModal={() =>
          setPropsState((prevState) => ({
            ...prevState,
            showManualActionModal: false,
          }))
        }
        showManualActionModal={propsState.showManualActionModal}
      />

      <ReasonsModal
        reasonModalProps={propsState.reasonModalProps}
        bankBoxPanelState={propsState}
        setBankBoxPanelState={setPropsState}
        fetchListData={fetchListData}
        closeReasonsModal={() =>
          setPropsState((prevState) => ({
            ...prevState,
            showReasonModal: false,
          }))
        }
        showReasonModal={propsState.showReasonModal}
      />

      <div className="container-fluid">
        <div className="row">
          <div className="main_wrapper">
            <div className="overlay" id="navigation-overly"></div>
            <section className="right_wrapper">
              <div className="panel-white">
                <div className="grayFiltr-bg brdr-FB clearfix">
                  <div className="col-md-12 ">
                    <h3 className="list_title clearfix">
                      <span className="pull-left">Bank Box Transactions</span>
                      <div className="pull-right font-18"></div>
                    </h3>
                  </div>
                </div>
                <div className="filter_flyout">
                  <div className="filter_box fm-filter filter-section-bx" id="">
                    <div className="row pad-L10">
                      <form id="form-filter-bankbox">
                        <div className="col-lg-2 col-md-2 pad-LR">
                          <div className="col-lg-6 col-md-6 pad-LR">
                            <SingleSelectDropDown
                              dataArr={BANKBOX_SEARCH_TYPE_ARR}
                              handleChange={handleFilterChange}
                              name="searchType"
                              value={filterValues.searchType}
                            ></SingleSelectDropDown>
                          </div>

                          <div className="col-lg-6 col-md-7 pad-LR">
                            <div className="form-group">
                              <input
                                type="text"
                                className="form-control "
                                name="searchTypeInput"
                                id="searchValue"
                                value={filterValues.searchTypeInput}
                                title="Loan ID"
                                placeholder={filterValues.searchType}
                                onChange={handleFilterChange}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-3 col-sm-3 col-xs-12 pad-LR">
                          <div className="col-lg-6 col-md-6 col-sm-6 mrg-all-0 sm-text-box">
                            <div
                              className="input-append date input-group input-sm"
                              id="dp2"
                            >
                              <DatePicker
                                selected={filterValues.date_from}
                                onChange={(date) =>
                                  handleDateChange(date, "date_from")
                                }
                                maxDate={new Date()}
                                placeholderText="Date From"
                                className="span2 form-control add-on calender filter-tb date-bg_input"
                                dateFormat="dd-MM-yyyy"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-6 mrg-all-0 sm-text-box">
                            <div
                              className="input-append date input-group input-sm"
                              id="dp3"
                            >
                              <DatePicker
                                selected={filterValues.date_to}
                                onChange={(date) =>
                                  handleDateChange(date, "date_to")
                                }
                                maxDate={new Date()}
                                placeholderText="Date To"
                                className="span2 form-control add-on calender filter-tb date-bg_input"
                                dateFormat="dd-MM-yyyy"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-1 col-md-1 col-sm-1 ms-drpdwn pad-LR">
                          <MultiSelectDropDownReset
                            data={dropdownData.statuses}
                            handleCheckboxChange={handleCheckboxChange}
                            toggleDropDown={toggleDropDown}
                            dropdownContainer={dropdownContainer}
                            title="Status"
                            filterName="statuses"
                            selectedDropdownData={filterValues.statuses}
                          ></MultiSelectDropDownReset>
                        </div>
                        <div className="col-lg-2 col-md-2 col-sm-2 ms-drpdwn pad-LR">
                          <SingleSelectDropDown
                            dataArr={dropdownData.banks}
                            handleChange={handleFilterChange}
                            name="banks"
                            value={filterValues.banks}
                          ></SingleSelectDropDown>
                        </div>
                        <div className="col-lg-3 col-md-3 col-sm-3 pad-LR">
                          <button
                            type="submit"
                            className="btn rc-btn_primary pad-L20 pad-R20 mar-R3 searchResults"
                            disabled={isLoading}
                            onClick={() => {
                              fetchListData();
                              getAllTransactionsCount();
                            }}
                          >
                            {isLoading ? "Please wait.." : "Search"}
                          </button>
                          <button
                            type="button"
                            className="btn rc-btn_rset pad-L20 pad-R20"
                            onClick={resetFunc}
                            disabled={isLoading}
                          >
                            Reset
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>

                <div className="container-fluid" id="list_data">
                  <div className="list-box mrg-T20 mrg-B20">
                    <div className="fixed-tabnavs filter_flyout dts_tab_navs">
                      <ul className="nav nav-tabs" role="tablist">
                        <li className={activeTab === 0 ? "active" : ""}>
                          <a href="/#" onClick={(e) => navSelected(e, 0)}>
                            Open
                            <RefreshSpin
                              isLoading={
                                activeTab === 0 ? isLoading : tabLoading
                              }
                              count={tabData.tabCounts[0]}
                            />
                          </a>
                        </li>

                        <li className={activeTab === 1 ? "active" : ""}>
                          <a href="/#" onClick={(e) => navSelected(e, 1)}>
                            In Progress
                            <RefreshSpin
                              isLoading={
                                activeTab === 1 ? isLoading : tabLoading
                              }
                              count={tabData.tabCounts[1]}
                            />
                          </a>
                        </li>
                        <li className={activeTab === 2 ? "active" : ""}>
                          <a href="/#" onClick={(e) => navSelected(e, 2)}>
                            On Hold
                            <RefreshSpin
                              isLoading={
                                activeTab === 2 ? isLoading : tabLoading
                              }
                              count={tabData.tabCounts[2]}
                            />
                          </a>
                        </li>
                        <li className={activeTab === 3 ? "active" : ""}>
                          <a href="/#" onClick={(e) => navSelected(e, 3)}>
                            Completed
                            <RefreshSpin
                              isLoading={
                                activeTab === 3 ? isLoading : tabLoading
                              }
                              count={tabData.tabCounts[3]}
                            />
                          </a>
                        </li>
                        <li className={activeTab === 4 ? "active" : ""}>
                          <a href="/#" onClick={(e) => navSelected(e, 4)}>
                            Cancelled/ Failed
                            <RefreshSpin
                              isLoading={
                                activeTab === 4 ? isLoading : tabLoading
                              }
                              count={tabData.tabCounts[4]}
                            />
                          </a>
                        </li>

                        <li className={activeTab === 6 ? "active" : ""}>
                          <a href="/#" onClick={(e) => navSelected(e, 6)}>
                            Reversed
                            <RefreshSpin
                              isLoading={
                                activeTab === 6 ? isLoading : tabLoading
                              }
                              count={tabData.tabCounts[6]}
                            />
                          </a>
                        </li>

                        <li className={activeTab === 5 ? "active" : ""}>
                          <a href="/#" onClick={(e) => navSelected(e, 5)}>
                            All Cases
                            <RefreshSpin
                              isLoading={
                                activeTab === 5 ? isLoading : tabLoading
                              }
                              count={tabData.tabCounts[5]}
                            />
                          </a>
                        </li>

                        <div className="pull-right"></div>
                      </ul>
                    </div>
                    <div
                      className="tab-content ad-fx-cls bankbox"
                      style={{ marginTop: "0px" }}
                    >
                      {!isLoading ? (
                        <BankboxTable
                          activeTab={activeTab}
                          page={page}
                          tabData={tabData}
                          sortingColumns={sortingColumns}
                          handleSorting={handleSorting}
                          fetchListData={fetchListData}
                          prevPage={prevPage}
                          nextPage={nextPage}
                          pageSelected={pageSelected}
                          bankBoxPanelState={propsState}
                          setBankBoxPanelState={setPropsState}
                        />
                      ) : (
                        <div
                          id="dtsMycase_processing"
                          className="dataTables_processing"
                          style={{
                            textAlign: "center",
                          }}
                        >
                          <img
                            src={`${process.env.REACT_APP_STYLES_PATH}loader.gif`}
                            alt=""
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <div className="arrowTop">
        {showArrowTopButton ? (
          <div className="pageToup">
            <button
              id="go-top"
              onClick={() =>
                window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
              }
            >
              <span className="fa fa-arrow-up" aria-hidden="true"></span>
            </button>
          </div>
        ) : (
          ""
        )}
      </div>
    </span>
  );
};

export default BankBoxPanel;
